package com.tregz.tremenz

import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.SideEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.runtime.withFrameNanos
import androidx.compose.ui.ExperimentalComposeUiApi
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.toArgb
import androidx.compose.ui.window.ComposeViewport
import com.tregz.tremenz.io.cloud.CloudSource
import com.tregz.tremenz.io.model.ModelData
import com.tregz.tremenz.io.model.ModelRepository
import com.tregz.tremenz.io.model.loadMixes
import com.tregz.tremenz.io.model.loadSongs
import com.tregz.tremenz.io.player.PlayerMedia
import com.tregz.tremenz.ui.theme.TregzTheme
import com.tregz.tremenz.ui.theme.toolbarColor
import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLMetaElement
import org.w3c.dom.events.EventListener

@OptIn(ExperimentalComposeUiApi::class)
fun main() {
    val container = (document.getElementById("root") as? HTMLElement) ?: document.body ?: return
    ComposeViewport(container) {
        // Playback state sits above the theme: switching themes in Safari reloads the page only when
        // nothing would be cut off.
        var currentMedia by remember { mutableStateOf<ModelData.Media?>(null) }
        var isVideoPlaying by remember { mutableStateOf(false) }
        var isPreparingVideo by remember { mutableStateOf(false) }
        var isSongPlaying by remember { mutableStateOf(false) }
        var isPreparingSong by remember { mutableStateOf(false) }

        // The theme follows the system day or night setting. Only a local build offers the toggle,
        // for debugging, and its saved choice overrides the system there.
        val isDebugHost = remember { isDebugHost() }
        var isNight by remember {
            mutableStateOf((if (isDebugHost) savedNight() else null) ?: systemNight.matches)
        }
        // The browser chrome takes the toolbar color, so the toolbar reads as filling the window.
        SideEffect { tintBrowser(container, isNight) }

        fun applyNight(night: Boolean) {
            if (night == isNight) return
            isNight = night
            // The Audio embeds do not report whether they play, so a selected one counts as busy.
            val isIdle = currentMedia !is ModelData.Audio &&
                !isVideoPlaying && !isPreparingVideo && !isSongPlaying && !isPreparingSong
            // Safari reads its toolbar color at page load only; the new theme applies on reload.
            if (isIdle && isSafari()) window.location.reload()
        }

        DisposableEffect(Unit) {
            val onSystemChange = EventListener {
                if (!isDebugHost || savedNight() == null) applyNight(systemNight.matches)
            }
            systemNight.addEventListener("change", onSystemChange)
            onDispose { systemNight.removeEventListener("change", onSystemChange) }
        }

        TregzTheme(
            darkTheme = isNight,
            onDarkThemeChange = if (isDebugHost) {
                { night ->
                    saveNight(night)
                    applyNight(night)
                }
            } else {
                null
            }
        ) {
            // The intro covers the page until Compose has drawn once, so a failed start is not blank.
            LaunchedEffect(Unit) {
                withFrameNanos { }
                document.getElementById("intro")?.remove()
            }
            var isContinuousMix by remember { mutableStateOf(true) }
            var hasAcceptedTerms by remember { mutableStateOf(isTermsAccepted()) }

            val player = remember { PlayerMedia() }
            val cloud = remember { CloudSource() }

            LaunchedEffect(cloud) {
                ModelRepository.loadMixes()
                ModelRepository.loadSongs()
                cloud.sync()
            }

            // `canplay` also fires after every seek and buffer refill, so only a freshly loaded
            // video may auto-start; later pauses must stay paused.
            var autoStartVideo by remember { mutableStateOf(false) }

            DisposableEffect(player) {
                val element = player.element
                val onCanPlay = EventListener {
                    if (autoStartVideo) {
                        autoStartVideo = false
                        player.start()
                    }
                }
                val onPlaying = EventListener {
                    isPreparingVideo = false
                    isVideoPlaying = true
                    element.style.visibility = "visible"
                }
                val onWaiting = EventListener {
                    isPreparingVideo = true
                    element.style.visibility = "hidden"
                }
                val onPause = EventListener {
                    if (!isPreparingVideo) isVideoPlaying = false
                }
                val onEnded = EventListener {
                    isVideoPlaying = false
                    isPreparingVideo = false
                    element.style.visibility = "hidden"
                }
                val onError = EventListener {
                    console.error("Video error on element", element.error)
                    autoStartVideo = false
                    isPreparingVideo = false
                    isVideoPlaying = false
                    element.style.visibility = "hidden"
                }
                element.addEventListener("canplay", onCanPlay)
                element.addEventListener("playing", onPlaying)
                element.addEventListener("waiting", onWaiting)
                element.addEventListener("pause", onPause)
                element.addEventListener("ended", onEnded)
                element.addEventListener("error", onError)
                onDispose {
                    element.removeEventListener("canplay", onCanPlay)
                    element.removeEventListener("playing", onPlaying)
                    element.removeEventListener("waiting", onWaiting)
                    element.removeEventListener("pause", onPause)
                    element.removeEventListener("ended", onEnded)
                    element.removeEventListener("error", onError)
                }
            }

            // True from an automatic advance until the new song reports PLAYING: the ended, ending
            // and ad signals of one song can all fire, and only the first may move the queue.
            var isAdvancePending by remember { mutableStateOf(false) }

            // Songs that failed back to back without one playing in between, capped at a queue lap.
            var consecutiveErrors by remember { mutableStateOf(0) }

            fun playNextSong(reason: String, force: Boolean = false) {
                val currentSong = currentMedia as? ModelData.Song ?: return
                if (isAdvancePending && !force) {
                    console.log("playNextSong (web): advance already pending (reason: $reason), ignoring")
                    return
                }
                // The active queue, not the whole catalogue: it is one channel's hour while the
                // Channels tab is driving playback, and every song otherwise.
                val songs = ModelRepository.ACTIVE_SONGS
                if (songs.isEmpty()) return
                val currentIndex = songs.indexOfFirst { it.id == currentSong.id }
                val nextIndex = if (currentIndex in 0 until songs.size - 1) currentIndex + 1 else 0
                val nextSong = songs[nextIndex]
                if (nextSong.id == currentSong.id) return
                isAdvancePending = true
                console.log("playNextSong (web): advancing to (${nextIndex + 1}/${songs.size}): ${nextSong.id} - ${nextSong.name} (reason: $reason)")
                currentMedia = nextSong
                isSongPlaying = true
            }

            App(
                currentMedia = currentMedia,
                player = player,
                isPreparingVideo = isPreparingVideo,
                isVideoPlaying = isVideoPlaying,
                onVideoPlayToggle = { play ->
                    if (play) {
                        autoStartVideo = false
                        player.start()
                        isVideoPlaying = true
                    } else {
                        player.pause()
                        isVideoPlaying = false
                    }
                },
                isPreparingSong = isPreparingSong,
                isSongPlaying = isSongPlaying,
                onSongPlayToggle = { isSongPlaying = it },
                onSongStateChange = { state ->
                    when (state) {
                        1 -> {
                            isAdvancePending = false
                            consecutiveErrors = 0
                            isPreparingSong = false
                            isSongPlaying = true
                        }
                        2 -> {
                            if (!isPreparingSong) isSongPlaying = false
                        }
                        3 -> isPreparingSong = true
                        0 -> {
                            isPreparingSong = false
                            isSongPlaying = false
                            if (isContinuousMix) {
                                playNextSong("ended")
                            }
                        }
                    }
                },
                onSongError = {
                    isPreparingSong = false
                    isSongPlaying = false
                    if (isContinuousMix) {
                        consecutiveErrors += 1
                        if (consecutiveErrors > ModelRepository.ACTIVE_SONGS.size) {
                            console.warn("A full queue lap failed (web), giving up")
                        } else {
                            playNextSong("error_$it", force = true)
                        }
                    }
                },
                onSongEnding = {
                    if (isContinuousMix) {
                        playNextSong("ending")
                    }
                },
                onSongAdDetected = {
                    if (isContinuousMix) {
                        playNextSong("ad_detected")
                    }
                },
                isContinuousMix = isContinuousMix,
                onContinuousMixToggle = { isContinuousMix = it },
                fsNotification = null,
                hasAcceptedTerms = hasAcceptedTerms,
                onAcceptTerms = {
                    hasAcceptedTerms = true
                    saveTermsAccepted()
                },
                onStopPlayback = {
                    player.stop()
                    isVideoPlaying = false
                    isPreparingVideo = false
                    isSongPlaying = false
                    isPreparingSong = false
                    currentMedia = null
                    autoStartVideo = false
                    isAdvancePending = false
                    consecutiveErrors = 0
                },
                onOpenLink = { url -> window.open(url, "_blank", "noopener,noreferrer") },
                onPlayMedia = { media ->
                    if (currentMedia == media) {
                        when (media) {
                            is ModelData.Song -> isSongPlaying = !isSongPlaying
                            is ModelData.Audio -> {}
                            else -> {
                                if (player.isPlaying) {
                                    player.pause()
                                    isVideoPlaying = false
                                } else {
                                    player.start()
                                    isVideoPlaying = true
                                }
                            }
                        }
                    } else {
                        val wasSongPlaying = isSongPlaying && currentMedia is ModelData.Song
                        currentMedia = media
                        autoStartVideo = false
                        // A pick of the user's own is a fresh start for the advance bookkeeping.
                        isAdvancePending = false
                        consecutiveErrors = 0
                        when (media) {
                            is ModelData.Song -> {
                                player.stop()
                                player.element.style.visibility = "hidden"
                                isVideoPlaying = false
                                isPreparingVideo = false
                                if (!wasSongPlaying) {
                                    isPreparingSong = true
                                }
                                isSongPlaying = true
                            }
                            is ModelData.Video -> {
                                isSongPlaying = false
                                isPreparingSong = false
                                isPreparingVideo = true
                                isVideoPlaying = false
                                player.element.style.visibility = "hidden"
                                val url = media.mediaUrl
                                if (url != null) {
                                    autoStartVideo = true
                                    player.load(url)
                                    player.start()
                                } else {
                                    isPreparingVideo = false
                                }
                            }
                            is ModelData.Audio -> {
                                player.stop()
                                player.element.style.visibility = "hidden"
                                isVideoPlaying = false
                                isPreparingVideo = false
                                isSongPlaying = false
                                isPreparingSong = false
                            }
                        }
                    }
                }
            )
        }
    }
}

private const val STORAGE_KEY_TERMS = "terms"

private fun isTermsAccepted(): Boolean {
    return try {
        window.localStorage.getItem(STORAGE_KEY_TERMS) == "true"
    } catch (_: Throwable) {
        false
    }
}

private fun saveTermsAccepted() {
    try {
        window.localStorage.setItem(STORAGE_KEY_TERMS, "true")
    } catch (_: Throwable) {
        // Storage access may be blocked in some browser settings
    }
}

private const val STORAGE_KEY_THEME = "theme"
private const val THEME_DAY = "day"

private val systemNight by lazy { window.matchMedia("(prefers-color-scheme: dark)") }

/** A local dev server, where the toolbar offers the day and night toggle for debugging. */
private fun isDebugHost(): Boolean = window.location.hostname.let { host ->
    host == "localhost" || host == "127.0.0.1" || host == "[::1]" || host.endsWith(".localhost")
}

/** The choice made with the debug toggle, or null to follow the system. */
private fun savedNight(): Boolean? {
    return try {
        window.localStorage.getItem(STORAGE_KEY_THEME)?.let { it != THEME_DAY }
    } catch (_: Throwable) {
        null
    }
}

private fun saveNight(isNight: Boolean) {
    try {
        window.localStorage.setItem(STORAGE_KEY_THEME, if (isNight) "night" else THEME_DAY)
    } catch (_: Throwable) {
        // Storage access may be blocked in some browser settings
    }
}

/**
 * Paints the browser around the canvas. `theme-color` tints the address bar where it is honoured
 * (Android, installed apps). Safari ignores it and copies the background of a fixed element at the
 * top edge into its toolbar, as it did with the previous website's fixed nav: the Compose root is
 * that element (see index.css), so it carries the toolbar color under the canvas.
 */
private fun tintBrowser(container: HTMLElement, isNight: Boolean) {
    val hex = toolbarColor(isNight).toCssHex()
    (document.querySelector("meta[name=theme-color]") as? HTMLMetaElement)?.content = hex
    (document.querySelector("meta[name=color-scheme]") as? HTMLMetaElement)?.content =
        if (isNight) "dark" else "light"
    container.style.background = hex
}

/**
 * WebKit's Safari, which ignores `theme-color` and samples its toolbar color once per load. Every
 * iOS browser is WebKit too, so their tokens (CriOS, FxiOS) still count.
 */
private fun isSafari(): Boolean {
    val agent = window.navigator.userAgent
    return "Safari" in agent && listOf("Chrome", "Chromium", "Android", "Edg").none { it in agent }
}

private fun Color.toCssHex(): String = "#" + (toArgb() and 0xFFFFFF).toString(16).padStart(6, '0')

